import { Fragment, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThemeProvider, createGlobalStyle } from 'styled-components'
import {
  Dimmer,
  Loader,
  ModalProps as SemanticModalProps,
} from 'semantic-ui-react'

import { Modal } from './Styles'
import { useTheme, useConfirmation, useRouter } from '@/hooks'
import closeModal from '@/helpers/close-modal'
import { HeaderCell } from '@/ui/Table/TableHeader/Styles'
import usePowerBarStore from '@/stores/usePowerBarStore'
import ErrorBoundary from '@/components/ErrorBoundary'

interface StyleProps {
  fullscreen?: boolean
  topbarOffset?: boolean
}

const GlobalStyle = createGlobalStyle<StyleProps>`
  .modals {
    padding: 0 !important;
  
    ${props => (!props.fullscreen ? 'margin-top: 0 !important;' : '')};
    ${props =>
      !props.fullscreen ? 'justify-content: center !important;' : ''};

    &&& {
      ${HeaderCell} {
        top: 0;
      }
    }
  }

  .modals.ui.dimmer.dimmer.dimmer {
    background-color: #012d3e20 !important;
  }

  ${({ theme }) => theme.mediaQueries.mobile} {
    .modals.ui.dimmer {
      overflow: hidden !important;
      top: ${props => (props.topbarOffset ? '55px' : '0px')} !important;
        
      /* the fixed top bar has a height of 51px */
      max-height: ${props =>
        props.topbarOffset ? 'calc(100% - 51px)' : '100%'};
    }      
  }
`

interface ModalProps extends Omit<SemanticModalProps, 'size'> {
  size?: 'fullscreen' | 'small' | 'mini' | 'tiny' | 'medium' | 'large' | 'full'
  loading?: boolean
  onClose?: () => void
  pathOnClose?: string
  children?: ReactNode
  canClose?: boolean
  closeOnDimmerClick?: boolean
  closeOnEscape?: boolean
  fullscreen?: boolean
  i18nOptions?: object
  defaultNS?: string
  reportNS?: string
  tReady?: any
  staticContext?: boolean
  loadingMessage?: string
  topbarOffset?: boolean
  isPage?: boolean
}

const ComposedModal = ({
  loading = false,
  onClose,
  pathOnClose,
  children,
  canClose = true,
  closeOnDimmerClick = true,
  closeOnEscape = true,
  fullscreen = true,
  i18nOptions,
  defaultNS,
  reportNS,
  tReady,
  staticContext,
  loadingMessage,
  topbarOffset,
  ...rest
}: ModalProps) => {
  const [open, setOpen] = useState(true)
  const { location, navigate } = useRouter()

  const { t } = useTranslation()

  const theme = useTheme()
  const confirm = useConfirmation()

  const handleOnClose = async () => {
    if (usePowerBarStore.getState().open) {
      return
    }

    if (!canClose) {
      await confirm({
        alertType: 'warning',
        title: t('Warning'),
        description: t('Are you sure you want to close this page?'),
      })
    }

    if (typeof onClose === 'function') {
      onClose()
      return
    }

    setOpen(false)

    closeModal(location, navigate, pathOnClose)
  }

  return (
    <Fragment>
      <GlobalStyle fullscreen={fullscreen} topbarOffset={topbarOffset} />

      <Modal
        closeOnEscape={closeOnEscape}
        style={{
          top: window.innerWidth > 992 ? '1rem' : '0',
        }}
        closeOnDimmerClick={closeOnDimmerClick}
        closeIcon={{
          name: 'close',
          size: 'big',
        }}
        open={open}
        onClose={handleOnClose}
        fullscreen={fullscreen}
        {...rest}>
        <ThemeProvider theme={theme}>
          <Fragment>
            {loading && (
              <Dimmer active inverted>
                <Loader>{loadingMessage || t('Loading page')}</Loader>
              </Dimmer>
            )}
            <ErrorBoundary modal location={location}>
              {children}
            </ErrorBoundary>
          </Fragment>
        </ThemeProvider>
      </Modal>
    </Fragment>
  )
}

ComposedModal.Header = Modal.Header
ComposedModal.Content = Modal.Content
ComposedModal.Description = Modal.Description
ComposedModal.Actions = Modal.Actions

export default ComposedModal
